import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { API_URL } from '../config/api';
import './History.css';

function History() {
  const { isAuthenticated, getToken } = useAuth();
  const navigate = useNavigate();
  const [generations, setGenerations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('recent'); // recent, oldest, matiere

  useEffect(() => {
    if (isAuthenticated) {
      fetchHistory();
    }
  }, [isAuthenticated]);

  const fetchHistory = async () => {
    setLoading(true);
    setError(null);

    try {
      const token = getToken();
      const response = await axios.get(`${API_URL}/generation/history`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setGenerations(response.data.generations);
      } else {
        setError('Impossible de récupérer l\'historique');
      }
    } catch (err) {
      console.error('Erreur fetch history:', err);
      setError(err.response?.data?.message || 'Erreur lors du chargement de l\'historique');
    } finally {
      setLoading(false);
    }
  };

  // Filtrage et tri
  const getFilteredAndSortedGenerations = () => {
    let filtered = generations;

    // Filtrage par recherche
    if (searchTerm) {
      filtered = filtered.filter(gen =>
        gen.matiere.toLowerCase().includes(searchTerm.toLowerCase()) ||
        gen.theme.toLowerCase().includes(searchTerm.toLowerCase()) ||
        gen.niveau.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Tri
    const sorted = [...filtered].sort((a, b) => {
      switch (sortBy) {
        case 'recent':
          return new Date(b.createdAt) - new Date(a.createdAt);
        case 'oldest':
          return new Date(a.createdAt) - new Date(b.createdAt);
        case 'matiere':
          return a.matiere.localeCompare(b.matiere);
        default:
          return 0;
      }
    });

    return sorted;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('fr-FR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const downloadFile = async (downloadUrl) => {
    try {
      // Construire l'URL complète en évitant la duplication de /api
      let fullUrl;
      if (downloadUrl.startsWith('http')) {
        fullUrl = downloadUrl;
      } else if (downloadUrl.startsWith('/api')) {
        // Si l'URL commence déjà par /api, utiliser directement (production)
        fullUrl = downloadUrl;
      } else {
        // Sinon, ajouter API_URL (développement)
        fullUrl = `${API_URL}${downloadUrl}`;
      }
      
      // Utiliser fetch pour récupérer le blob avec le token d'authentification
      const token = getToken();
      const response = await fetch(fullUrl, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Erreur lors du téléchargement');
      }
      
      // Récupérer le blob
      const blob = await response.blob();
      
      // Créer un lien temporaire pour télécharger le blob
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Extraire le nom du fichier depuis le header Content-Disposition ou utiliser un nom par défaut
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = 'fichier';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, '');
        }
      }
      
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Libérer l'URL du blob
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Erreur lors du téléchargement du fichier:', error);
      alert('Erreur lors du téléchargement. Veuillez réessayer.');
    }
  };

  const downloadAll = async (downloadAllUrl) => {
    try {
      // Construire l'URL complète en évitant la duplication de /api
      let fullUrl;
      if (downloadAllUrl.startsWith('http')) {
        fullUrl = downloadAllUrl;
      } else if (downloadAllUrl.startsWith('/api')) {
        // Si l'URL commence déjà par /api, utiliser directement (production)
        fullUrl = downloadAllUrl;
      } else {
        // Sinon, ajouter API_URL (développement)
        fullUrl = `${API_URL}${downloadAllUrl}`;
      }
      
      // Pour un ZIP, utiliser fetch pour récupérer le blob et déclencher le téléchargement
      const token = getToken();
      const response = await fetch(fullUrl, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Erreur lors du téléchargement');
      }
      
      // Récupérer le blob
      const blob = await response.blob();
      
      // Créer un lien temporaire pour télécharger le blob
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Extraire le nom du fichier depuis le header Content-Disposition ou utiliser un nom par défaut
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = 'seance_complete.zip';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, '');
        }
      }
      
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Libérer l'URL du blob
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Erreur lors du téléchargement du ZIP:', error);
      alert('Erreur lors du téléchargement. Veuillez réessayer.');
    }
  };

  const getFormatIcon = (format) => {
    const icons = {
      pdf: 'PDF',
      docx: 'DOCX',
      tex: 'TEX',
      md: 'MD',
      pptx: 'PPTX'
    };
    return icons[format] || format.toUpperCase();
  };

  // Fonction pour régénérer un cours avec les mêmes paramètres
  const handleRegenerate = (gen) => {
    // Parser la répartition pour extraire les pourcentages
    const repartitionMatch = gen.repartition.match(/Cours:(\d+)%,Exemples:(\d+)%,Exercices:(\d+)%/);
    let coursPct = 70, exemplesPct = 15, exercicesPct = 15;
    if (repartitionMatch) {
      coursPct = parseInt(repartitionMatch[1]);
      exemplesPct = parseInt(repartitionMatch[2]);
      exercicesPct = parseInt(repartitionMatch[3]);
    }

    // Préparer les formats sélectionnés
    // formatsSelected peut être un objet JSON ou une chaîne JSON
    let formats = {
      tex: false,
      pdf: true,
      docx: false,
      md: false
    };
    
    if (gen.formatsSelected) {
      try {
        formats = typeof gen.formatsSelected === 'string' 
          ? JSON.parse(gen.formatsSelected) 
          : gen.formatsSelected;
      } catch (e) {
        console.warn('Erreur parsing formatsSelected:', e);
      }
    }

    // Préparer les données pour le formulaire
    const regenerateData = {
      MATIERE: gen.matiere,
      NIVEAU: gen.niveau,
      DUREE: gen.duree.toString(),
      THEME: gen.theme,
      NB_PAGES: gen.nbPages.toString(),
      PLAN_COURS: '',
      LANGUE: gen.langue || 'Français',
      COURS_PCT: coursPct,
      EXEMPLES_PCT: exemplesPct,
      EXERCICES_PCT: exercicesPct,
      formats: formats
    };

    // Naviguer vers la page de génération avec les paramètres
    navigate('/', { state: { regenerateData } });
  };

  if (!isAuthenticated) {
    return (
      <div className="history-container">
        <div className="alert alert-info">
          <h2>Connexion requise</h2>
          <p>Vous devez être connecté pour accéder à l'historique de vos cours.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="history-container">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Chargement de votre historique...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="history-container">
        <div className="alert alert-error">
          <strong>Erreur :</strong> {error}
          <button onClick={fetchHistory} className="retry-button">
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  const filteredGenerations = getFilteredAndSortedGenerations();

  return (
    <div className="history-container">
      <div className="history-header">
        <h1>Mes Cours Générés</h1>
        <p className="history-subtitle">
          Retrouvez tous vos cours générés et téléchargez-les à nouveau
        </p>
      </div>

      {/* Barre de contrôle */}
      <div className="history-controls">
        <div className="search-box">
          <input
            type="text"
            placeholder="Rechercher par matière, thème ou niveau..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>

        <div className="sort-box">
          <label htmlFor="sort" style={{ color: "white" }}>Trier par :</label>
          <select
            id="sort"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="sort-select"
          >
            <option value="recent">Plus récent</option>
            <option value="oldest">Plus ancien</option>
            <option value="matiere">Matière (A-Z)</option>
          </select>
        </div>
      </div>

      {/* Statistiques */}
      <div className="history-stats">
        <div className="stat-card">
          <div className="stat-content">
            <span className="stat-value">{generations.length}</span>
            <span className="stat-label">Cours générés</span>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-content">
            <span className="stat-value">
              {generations.filter(g => g.filesAvailable).length}
            </span>
            <span className="stat-label">Fichiers disponibles</span>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-content">
            <span className="stat-value">
              {new Set(generations.map(g => g.matiere)).size}
            </span>
            <span className="stat-label">Matières</span>
          </div>
        </div>
      </div>

      {/* Liste des générations */}
      {filteredGenerations.length === 0 ? (
        <div className="empty-state">
          <h2>Aucun cours trouvé</h2>
          <p>
            {searchTerm
              ? 'Aucun cours ne correspond à votre recherche.'
              : 'Vous n\'avez pas encore généré de cours.'}
          </p>
        </div>
      ) : (
        <div className="generations-list">
          {filteredGenerations.map((gen) => (
            <div key={gen.id} className="generation-card">
              <div className="generation-header">
                <div className="generation-title">
                  <h3>{gen.theme}</h3>
                  <span className="generation-matiere">{gen.matiere}</span>
                </div>
                <span className="generation-date">
                  {formatDate(gen.createdAt)}
                </span>
              </div>

              <div className="generation-details">
                {/* Ligne 1 : Niveau */}
                <div className="detail-line">
                  <div className="detail-row">
                    <span className="detail-label">Niveau :</span>
                    <span className="detail-value">{gen.niveau}</span>
                  </div>
                </div>

                {/* Langue */}
                <div className="detail-line">
                  <div className="detail-row">
                    <span className="detail-label">Langue :</span>
                    <span className="detail-value">{gen.langue || 'Français'}</span>
                  </div>
                </div>
                
                {/* Ligne 2 : Durée, Pages */}
                <div className="detail-line detail-line-group">
                  <div className="detail-row">
                    <span className="detail-label">Durée :</span>
                    <span className="detail-value">{gen.duree} minutes</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Pages :</span>
                    <span className="detail-value">{gen.nbPages} pages</span>
                  </div>
                </div>
                
                {/* Ligne 3 : Répartition */}
                <div className="detail-line">
                  <div className="detail-row detail-row-repartition">
                    <span className="detail-label">Répartition :</span>
                    <span className="detail-value">{gen.repartition}</span>
                  </div>
                </div>
              </div>

              <div className="generation-actions">
                <button
                  onClick={() => handleRegenerate(gen)}
                  className="regenerate-button"
                  title="Modifier les paramètres et régénérer ce cours"
                >
                  Modifier les paramètres
                </button>
              </div>

              {gen.filesAvailable ? (
                <div className="generation-files">
                  <h4>Fichiers disponibles :</h4>
                  <div className="files-grid">
                    {gen.files.map((file, idx) => (
                      <button
                        key={idx}
                        onClick={() => downloadFile(file.downloadUrl)}
                        className="file-download-button"
                        title={`Télécharger ${file.filename}`}
                      >
                        <span className="file-format">{getFormatIcon(file.format)}</span>
                      </button>
                    ))}
                  </div>
                  <button
                    onClick={() => downloadAll(gen.downloadAllUrl)}
                    className="download-all-button"
                  >
                    Télécharger tout (ZIP)
                  </button>
                </div>
              ) : (
                <div className="generation-files-unavailable">
                  <p>Fichiers non disponibles (session expirée)</p>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default History;

